<template>
  <b-row>
    <b-col v-if="waiting_for_importer && !error" class="mt-3 mb-4">
      <div class="d-flex justify-content-center mb-3 mt-5">
        <h4>
          Reading Soundslides file and extracting media files ...
        </h4>
      </div>
      <div class="d-flex justify-content-center mb-3 mt-5">
        <b-spinner
          style="width: 3rem; height: 3rem;"
          type="grow"
          size="lg"
          variant="primary"
        />
      </div>
    </b-col>
    <b-col v-if="error" class="mt-3 mb-4">
      <div class="col-6 offset-3">
        <h3>
          Your show could not be imported.
        </h3>
        <p class="mt-3">
          {{ error_message }}
        </p>
        <p>
          Please include your show reference number (#{{ show.key }}) when
          contacting support.
        </p>
      </div>
    </b-col>
    <b-col v-if="!waiting_for_importer && !error" class="mt-3 mb-4">
      <h5>Upload project folder</h5>
      <p>
        Select your project's folder.
      </p>

      <p class="small mt-3">
        If you don't have your original project folder, you can upload your
        show's
        <b-link
          :to="{ name: 'show-import-zip', params: { id: show.key } }"
          v-text="'zip file'"
        />.
      </p>

      <b-row v-show="!upload_inprogress">
        <b-col md="6">
          <b-form-file
            v-model="file_list"
            :directory="true"
            :file-name-formatter="formatNames"
            multiple
            placeholder="Select a folder containing a Soundslides show ..."
            @input="fileInput"
          />
        </b-col>
      </b-row>
      <b-row>
        <b-col>
          <div v-show="uploaded_percentage != 0" class="mt-3">
            <!-- Uploading. -->
            <p v-show="file != null && uploaded_percentage == 0" class="mt-3">
              <b-btn variant="primary" @click="submitFile()">Submit</b-btn>
            </p>
            <p v-show="uploaded_percentage != 0" class="alert alert-success">
              {{ uploaded_percentage }}% uploaded
            </p>
            <b-progress
              v-show="uploaded_percentage != 0"
              variant="success"
              :value="uploaded_percentage"
              :max="100"
              class="mb-3"
            />
          </div>
          <div v-show="zipping_inprogress" class="mt-3">
            <!-- Zipping. -->
            <p class="alert alert-success">
              Preparing upload ...
              {{ zipProgressPercent }}%
            </p>
            <b-spinner label="Spinning" />
          </div>
        </b-col>
      </b-row>
      <b-row v-if="!upload_inprogress">
        <b-col md="12">
          <div v-for="(group, gidx) in groupedShows" :key="'group' + gidx">
            <div v-for="(projectShow, idx) in group" :key="'show' + idx">
              <ProjectPreview
                :show_obj="projectShow"
                :callback="callback"
                :possible-duplicate="idx > 0"
                class="mt-5"
              />
            </div>
          </div>
        </b-col>
      </b-row>
      <b-row v-if="!upload_inprogress && dirParsed && shows.length == 0">
        <b-col md="12" class="mt-5">
          <h3>No shows found within that folder.</h3>
        </b-col>
      </b-row>

      <b-row v-if="!upload_inprogress && zips.length > 0" class="mt-4">
        <b-col md="6">
          <p v-if="zips.length == 1" class="alert alert-info">
            An additional zip file was also found within this directory.
            Typically, these are the smaller 'publish_to_web.zip' files. If you
            do not see the slide show you were expecting above, you may want to
            try importing this
            <b-link
              :to="{ name: 'show-import-zip', params: { id: show.key } }"
              v-text="'zip file'"
            />.
          </p>

          <p v-else class="alert alert-info">
            {{ zips.length }} additional zip files were also found within this
            directory. Typically, these are the smaller 'publish_to_web.zip'
            files. If you do not see the slide show you were expecting above,
            you may want to try importing these
            <b-link
              :to="{ name: 'show-import-zip', params: { id: show.key } }"
              v-text="'zip files'"
            />.
          </p>

          <!--
          <div v-for="(zip, idx) in zips" :key="'zip' + idx">
            {{ zip }}
          </div>
        -->
        </b-col>
      </b-row>
    </b-col>
  </b-row>
</template>

<script>
import axios from "axios";
import { DirectoryParser } from "@/models/directoryShow";
import ProjectPreview from "@/components/showImportAsZip/ProjectPreview.vue";
import reject from "lodash/reject";
import map from "lodash/map";
import each from "lodash/each";
import groupBy from "lodash/groupBy";
import orderBy from "lodash/orderBy";
import { db } from "@/fstore";
import { processXmlDataToShow } from "@/utils/legacyData";
import { processTxtDataToShow } from "@/utils/legacyData";
import JSZip from "jszip";
//import { saveAs } from "file-saver";
export default {
  name: "UploadPublishToWebZip",
  components: {
    ProjectPreview
  },
  props: {
    show: {
      type: Object,
      required: true,
      default: () => {}
    }
  },
  data() {
    return {
      key: "",
      fields: {},
      file: null,
      size: 0,
      zip_name: "",
      uploaded_percentage: 0,
      file_list: [],
      show_details: {},
      upload_inprogress: false,
      image_queue: [],
      has_audio: false,
      waiting_for_importer: false,
      shows: [],
      uploadShow: null,
      zipping_inprogress: false,
      dirParsed: false,
      zips: [],
      zipProgressPercent: 0
    };
  },
  computed: {
    ready() {
      return this.key !== "";
    },
    authorized() {
      return this.$store.state.authorized;
    },
    user_plan() {
      return this.$store.getters.user_plan;
    },
    shows_in_progress() {
      return reject(this.$store.state.legacy_shows, { video_completed: true })
        .length;
    },
    import_by_url_complete() {
      return this.show.import_by_url_complete;
    },
    import_by_url_sns_attempt() {
      return this.show.import_by_url_sns_attempt;
    },
    error() {
      return this.show.import_by_url_error;
    },
    error_message() {
      return this.show.import_by_url_error_message;
    },
    groupedShows() {
      return groupBy(orderBy(this.shows, "-averageImageSize"), "controlHash");
    }
  },
  watch: {
    file() {
      this.handleFileUpload();
    },
    import_by_url_complete(value) {
      if (value) {
        console.log("importComplete!");
        this.importComplete();
      }
    },
    import_by_url_sns_attempt(value) {
      if (
        !this.import_by_url_complete &&
        this.show.import_by_url_safe_for_retry
      ) {
        //var ms_delay_before_retry = 5000;
        console.log("attempts", value);
        //console.log("Possibly retry the import!");
        //setTimeout(this.send_importer_sns, ms_delay_before_retry); // Wait 2 seconds, then resend the SNS.
      }
    }
  },
  mounted() {
    console.log(this.show);
    if (this.show.import_by_url_complete) {
      // Go to main show page.
      this.importComplete();
    }
    if (
      this.show.import_by_url &&
      !this.show.import_by_url_complete &&
      !this.show.import_by_url_error
    ) {
      // Wait.
      this.waiting_for_importer = true;
      this.send_importer_sns(); // Fire it again, what's the harm.
    }
  },
  methods: {
    fileInput(e) {
      console.log(e);
      this.dirParsed = false;
      var dirParse = new DirectoryParser(this.file_list);
      this.shows = dirParse.shows;
      this.zips = dirParse.zips;
      this.dirParsed = true;
      each(this.shows, s => {
        s.parseControlFile()
          .then(show => {
            return show.extractFilesToBlobs();
          })
          .then(show => {
            console.log(show.slideArray);
          });
      });
      this.get_policy_details();
    },
    formatNames(files) {
      return files.length === 1
        ? files[0].name
        : `${files.length} files selected`;
    },
    importComplete() {
      var _v = this;
      _v.$router.push({ name: "show", params: { id: _v.show.key } });
      // This is now (20200403) done in the lambda, so not important.
    },
    callback(show) {
      console.log("callback", show);
      this.uploadShow = show;
      this.createZipBlob();
      this.upload_inprogress = true;
    },
    createZipBlob() {
      this.zipping_inprogress = true;
      var _v = this;
      var zip = new JSZip();
      // Create the image directory.
      var dirname = "fullscreen";
      zip.folder(dirname);
      each(this.uploadShow.slideArray, s => {
        console.log("Adding", s.file);
        zip.file(`${dirname}/${s.file}`, s.fileObj);
      });
      if (this.uploadShow.audioBlob) {
        zip.file(`audio_hi.mp3`, this.uploadShow.audioFileObj);
      }
      if (this.uploadShow.xmlString) {
        zip.file(`soundslide.xml`, this.uploadShow.xmlString);
      }
      if (this.uploadShow.txtString) {
        zip.file(`soundslide.txt`, this.uploadShow.txtString);
      }

      zip
        .generateAsync({ type: "blob" }, function updateCallback(metadata) {
          _v.zipProgressPercent = metadata.percent.toFixed(0);
          console.log("progression: " + metadata.percent.toFixed(2) + " %");
          if (metadata.currentFile) {
            console.log("current file = " + metadata.currentFile);
          }
        })
        .then(function(blob) {
          _v.zipBlob = blob;
          _v.zipping_inprogress = false;
          _v.processData();
        });
    },

    processData() {
      var _v = this;

      // Deal with the xmlString or the txtString.
      if (this.uploadShow.xmlString) {
        processXmlDataToShow(_v.show, this.uploadShow.xmlString);
        _v.$store.dispatch("editor/updateShowData", _v.show);
        // Add each image to the queue.
        if (_v.show.data.displaySettings.noAudio) {
          // No audio.
        } else {
          _v.has_audio = true;
        }
      } else {
        processTxtDataToShow(_v.show, this.uploadShow.txtString);
        _v.$store.dispatch("editor/updateShowData", _v.show);
        if (_v.show.data.displaySettings.noAudio) {
          // No audio.
        } else {
          _v.has_audio = true;
        }
      }
      console.log("processData complete!");

      // Process needed files into zip.

      _v.submitFile();
    },
    submitFile() {
      var _v = this;
      let formData = new FormData();
      for (const [key, value] of Object.entries(this.fields)) {
        console.log(key, value);
        // Don't add key, we want the slug as the name.
        if (key != "key") {
          formData.append(key, value);
        }
      }
      formData.append("key", this.key); // Necessary as fields key includes ${filename}
      formData.append("acl", "public-read");
      formData.append("success_action_status", "201");
      formData.append("Content-Type", "application/zip");
      formData.append("file", this.zipBlob);
      console.log(">> formData >> ", formData);

      // You should have a server side REST API
      axios
        .post(
          "https://soundslides-video-converter-post.s3.amazonaws.com/",
          formData,
          {
            onUploadProgress: progressEvent => {
              console.log(progressEvent);
              var _uploaded = Math.ceil(
                (progressEvent.loaded / progressEvent.total) * 100
              );
              _v.uploaded_percentage = _uploaded;
            }
          }
        )
        .then(function(e) {
          console.log(e);
          console.log("SUCCESS!!");
          _v.complete();
        });
    },
    get_policy_details() {
      var _v = this;
      var policyEndpoint =
        "https://upload.soundslides.com/policy/v4/converter/";
      //policyEndpoint = "http://127.0.0.1:5000/policy/v4/converter/";
      axios
        .get(policyEndpoint)
        .then(function(response) {
          // handle success
          console.log(response);
          _v.key = response.data.filename;
          _v.fields = response.data.fields;
        })
        .catch(function(error) {
          // handle error
          console.log(error);
        });
    },
    send_importer_sns() {
      // At this point, call the https://importer-v1-to-v3.soundslides.com/import/{$show_key}/sns
      // You likely will need to poll it a few times, as there is a likely race condition between
      // all the lambdas, and Firestore.
      console.log("send_importer_sns");
      var _v = this;
      this.waiting_for_importer = true;

      var show_key = this.show.key;
      var importer_url =
        "https://importer-v1-to-v3.soundslides.com/import/" + show_key + "/sns";
      //importer_url = "http://127.0.0.1:5000/import/" + show_key + "/sns";
      axios
        .get(importer_url)
        .then(response => {
          console.log("response", response);
        })
        .catch(response => {
          console.log("response", response);
          _v.error = true;
          _v.error_message = "An undefined error occurred.";
        });
    },
    complete() {
      // Save id to FB.
      var _v = this;
      console.log("complete!");
      var import_by_url_path = `https://hosting.soundslides.com/${this.key}`;
      var show_key = this.show.key;
      var update_obj = {};
      update_obj.key = show_key;
      update_obj.import_by_url = true;
      update_obj.import_by_url_path = import_by_url_path;
      update_obj.import_by_url_inprogress = false;
      update_obj.import_by_url_complete = false;
      update_obj.import_by_url_has_audio = this.has_audio;
      update_obj["data.assetsAvailable"] = false;
      var images = map(this.show.data.slideArray, s => {
        return {
          filename: s.file,
          name: s.file,
          source_id: show_key
        };
      });
      update_obj["data.images"] = images;
      // It needs a general "images" array!
      if (this.has_audio) {
        update_obj["data.audio.filename"] = "audio_hi.mp3.mp3";
        update_obj["data.audio.name"] = "audio_hi.mp3";
        update_obj["data.audio.source_id"] = show_key;
        update_obj["data.audio.deployed"] = false;
      }
      // Update on server.
      db.collection("editor_shows")
        .doc(show_key)
        .update(update_obj)
        .then(function() {
          console.log("updateForImportByUrl", "success");
          _v.waiting_for_importer = true;
          // Trigger the first call.
          _v.send_importer_sns();
        });
    }
  }
};
</script>

<style lang="scss" scoped></style>
